import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import { IoClose } from "react-icons/io5";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { PREMIUM_PLANS } from "../constants/premiumPlans";
import {
  buildComplianceAttestationPayload,
  isDatingProhibitedCountry,
} from "../constants/datingCompliance";
import {
  calculateAge,
  maxBirthDateForMinAge,
  minBirthDateForMaxAge,
} from "../util/calculateAge";
import { GetUserById, RecordComplianceAttestation, UpdateUser } from "../services/backend";
import { canStartStripeCheckout, startStripeCheckout } from "../services/stripeCheckout";
import BirthDatePicker from "./ui/BirthDatePicker";

const PLAN_ORDER = ["monthly", "yearly"];

const INSTAGRAM_URL =
  process.env.REACT_APP_INSTAGRAM_Link?.trim() || "https://www.instagram.com/payinapo/";

function PlanCard({ planKey, plan, selected, onSelect, t }) {
  const isYearly = planKey === "yearly";
  const isMonthly = planKey === "monthly";
  const hasMonthlyTrial = isMonthly && Number(plan.trialDays) > 0;
  const showMonthlyPromo =
    isMonthly && plan.promo && plan.regularAmount && plan.regularAmount !== plan.amount;

  const card = (
    <Box
      as="button"
      type="button"
      onClick={() => onSelect(planKey)}
      w="100%"
      textAlign="left"
      position="relative"
      borderRadius="2xl"
      border="2px solid"
      borderColor={selected ? "brand.500" : "whiteAlpha.100"}
      bg={selected ? "rgba(163,230,53,0.1)" : "rgba(255,255,255,0.04)"}
      px={4}
      py={3.5}
      transition="all 0.18s ease"
      _hover={{ bg: selected ? "rgba(163,230,53,0.14)" : "rgba(255,255,255,0.06)" }}
    >
      {isYearly ? (
        <Box
          position="absolute"
          top={0}
          right={4}
          transform="translateY(-50%)"
          zIndex={1}
          display="inline-flex"
          alignItems="center"
          px={2.5}
          py={1}
          borderRadius="full"
          bg="brand.500"
          color="white"
          fontSize="9px"
          fontWeight="bold"
          letterSpacing="0.08em"
          textTransform="uppercase"
          lineHeight="1"
          boxShadow="0 2px 8px rgba(0,0,0,0.28)"
        >
          {t("paywall.bestValue")}
        </Box>
      ) : null}

      {showMonthlyPromo ? (
        <Box
          position="absolute"
          top={0}
          right={4}
          transform="translateY(-50%)"
          zIndex={1}
          display="inline-flex"
          alignItems="center"
          px={2.5}
          py={1}
          borderRadius="full"
          bg="brand.500"
          color="gray.900"
          fontSize="9px"
          fontWeight="bold"
          letterSpacing="0.08em"
          textTransform="uppercase"
          lineHeight="1"
          boxShadow="0 2px 8px rgba(0,0,0,0.28)"
        >
          {t("paywall.limitedTimePromo")}
        </Box>
      ) : null}

      <Flex align="center" justify="space-between" gap={3}>
        <Flex align="center" gap={2} flexShrink={1} minW={0}>
          <Text as="span" fontSize="md" fontWeight="bold" color="white" lineHeight="1" flexShrink={0}>
            {t(isYearly ? "paywall.yearly" : "paywall.monthly")}
          </Text>
          {hasMonthlyTrial ? (
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              px={2.5}
              py={1}
              borderRadius="full"
              border="1.5px solid"
              borderColor="brand.500"
              bg="transparent"
              color="brand.300"
              fontSize="11px"
              fontWeight="bold"
              letterSpacing="0.05em"
              textTransform="uppercase"
              lineHeight="1.2"
              whiteSpace="nowrap"
            >
              {t("paywall.monthlyTrialBadge", { days: plan.trialDays })}
            </Box>
          ) : null}
        </Flex>

        <Box textAlign="right" flexShrink={0}>
          {isYearly ? (
            <>
              <Text as="span" fontSize="lg" fontWeight="bold" color="white" lineHeight="1.2">
                ${plan.amount}
                <Text as="span" fontSize="sm" color="whiteAlpha.600" fontWeight="medium">
                  {t("paywall.perYearShort")}
                </Text>
              </Text>
              <Text fontSize="xs" color="whiteAlpha.500" fontWeight="medium" mt={0.5}>
                {t("paywall.yearlyMonthlyBreakdown", { monthly: plan.monthlyEquivalent })}
              </Text>
            </>
          ) : (
            <>
              <Flex align="baseline" justify="flex-end" gap={1.5}>
                {showMonthlyPromo ? (
                  <Text
                    as="span"
                    fontSize="sm"
                    color="whiteAlpha.500"
                    fontWeight="medium"
                    textDecoration="line-through"
                    lineHeight="1"
                  >
                    ${plan.regularAmount}
                  </Text>
                ) : null}
                <Text as="span" fontSize="lg" fontWeight="bold" color="white" lineHeight="1">
                  ${plan.amount}
                  <Text as="span" fontSize="sm" color="whiteAlpha.600" fontWeight="medium">
                    {t("paywall.perMonthShort")}
                  </Text>
                </Text>
              </Flex>
              {showMonthlyPromo ? (
                <Text fontSize="xs" color="brand.300" fontWeight="semibold" mt={0.5}>
                  {t("paywall.limitedTimePriceNote")}
                </Text>
              ) : null}
            </>
          )}
        </Box>
      </Flex>
    </Box>
  );

  if (!isYearly && !showMonthlyPromo) return card;

  return (
    <Box position="relative" w="100%" pt={2}>
      {card}
    </Box>
  );
}

export default function PaywallModal({ isOpen, onClose, userId, size }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const [purchaseConsent, setPurchaseConsent] = useState(false);
  const [profileCountry, setProfileCountry] = useState("");
  const [geoBlocked, setGeoBlocked] = useState(false);
  const [needsBirthDate, setNeedsBirthDate] = useState(false);
  const [birthDate, setBirthDate] = useState("");

  const maxBirthDate = useMemo(() => maxBirthDateForMinAge(18), []);
  const minBirthDate = useMemo(() => minBirthDateForMaxAge(100), []);
  const birthAge = birthDate ? calculateAge(birthDate) : null;
  const birthDateValid = birthAge !== null && birthAge >= 18 && birthAge <= 100;

  const responsiveSize = useBreakpointValue({ base: "md", sm: "md" }) ?? "md";
  const modalSize = size === "2xl" || size === "4xl" ? "md" : (size ?? responsiveSize);
  const checkoutReady = canStartStripeCheckout();
  const selectedPlanData = PREMIUM_PLANS[selectedPlan];
  const billingNote =
    selectedPlan === "yearly"
      ? t("paywall.billingDisclaimerYearly", { amount: selectedPlanData.amount })
      : Number(selectedPlanData.trialDays) > 0
        ? selectedPlanData.promo &&
          selectedPlanData.regularAmount &&
          selectedPlanData.regularAmount !== selectedPlanData.amount
          ? t("paywall.billingDisclaimerMonthlyPromo", {
              amount: selectedPlanData.amount,
              regular: selectedPlanData.regularAmount,
              days: selectedPlanData.trialDays,
            })
          : t("paywall.billingDisclaimerMonthly", {
              amount: selectedPlanData.amount,
              days: selectedPlanData.trialDays,
            })
        : t("paywall.billingDisclaimerMonthlyNoTrial", {
            amount: selectedPlanData.amount,
          });
  const continueLabel =
    selectedPlan === "yearly"
      ? t("paywall.continueYearly", { amount: selectedPlanData.amount })
      : Number(selectedPlanData.trialDays) > 0
        ? t("paywall.continueMonthlyTrial", { days: selectedPlanData.trialDays })
        : t("paywall.continueMonthly", { amount: selectedPlanData.amount });

  useEffect(() => {
    if (!isOpen || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const profile = await GetUserById(userId);
        if (cancelled || !profile) return;
        setProfileCountry(profile.country || "");
        setGeoBlocked(isDatingProhibitedCountry(profile.country));
        if (profile.termsAcceptedAt && profile.ageAttestedAt) {
          setPurchaseConsent(true);
        }
        const existingAge = calculateAge(profile.birthDate);
        if (existingAge !== null && existingAge >= 18) {
          setNeedsBirthDate(false);
          setBirthDate(profile.birthDate || "");
        } else {
          setNeedsBirthDate(true);
          setBirthDate("");
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  const handleContinue = useCallback(async () => {
    const plan = PREMIUM_PLANS[selectedPlan];
    if (!plan?.priceId) {
      toast({
        title: t("paywall.checkoutNotReady"),
        description: t("paywall.priceIdsMissing"),
        status: "warning",
        duration: 6000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    if (!userId) {
      toast({
        title: t("paywall.checkoutError"),
        description: t("paywall.signInRequired"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    if (geoBlocked || isDatingProhibitedCountry(profileCountry)) {
      toast({
        title: t("paywall.geoBlockedTitle"),
        description: t("paywall.geoBlockedBody"),
        status: "error",
        duration: 7000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    if (!purchaseConsent) {
      toast({
        title: t("paywall.consentRequired"),
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    if (needsBirthDate && !birthDateValid) {
      toast({
        title: t("paywall.birthDateRequired"),
        description: t("paywall.birthDateRequiredHint"),
        status: "warning",
        duration: 6000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setBusy(true);
    try {
      let profile = await GetUserById(userId);
      let age = calculateAge(profile?.birthDate);

      if (age === null || age < 18) {
        if (!birthDateValid) {
          throw new Error(t("paywall.birthDateRequiredHint"));
        }
        const ageToSave = calculateAge(birthDate);
        await UpdateUser({
          Id: userId,
          BirthDate: birthDate,
          Age: ageToSave,
        });
        profile = await GetUserById(userId);
        age = calculateAge(profile?.birthDate);
        try {
          const stored = JSON.parse(localStorage.getItem("user") || "null") || {};
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...stored,
              ...(profile || {}),
              birthDate: profile?.birthDate || birthDate,
              age: ageToSave,
            })
          );
        } catch {
          /* ignore */
        }
      }

      if (age === null || age < 18) {
        throw new Error(t("paywall.ageRequired"));
      }
      if (isDatingProhibitedCountry(profile?.country)) {
        throw new Error(t("paywall.geoBlockedBody"));
      }
      if (!profile?.termsAcceptedAt || !profile?.ageAttestedAt) {
        await RecordComplianceAttestation(
          userId,
          buildComplianceAttestationPayload({ accepted: true })
        );
      }
      await startStripeCheckout({ priceId: plan.priceId, userId });
    } catch (error) {
      toast({
        title: t("paywall.checkoutError"),
        description: error.message || t("paywall.checkoutFailed"),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "top",
      });
      setBusy(false);
    }
  }, [
    birthDate,
    birthDateValid,
    geoBlocked,
    needsBirthDate,
    profileCountry,
    purchaseConsent,
    selectedPlan,
    t,
    toast,
    userId,
  ]);  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={modalSize}
      isCentered
      scrollBehavior="inside"
      closeOnOverlayClick
      closeOnEsc
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.850" backdropFilter="blur(14px)" />
      <ModalContent
        mx={{ base: 4, sm: 4 }}
        my="auto"
        maxH={{ base: "min(92dvh, 92vh)", sm: "90vh" }}
        maxW={{ base: "calc(100% - 32px)", sm: "460px" }}
        borderRadius="3xl"
        overflow="hidden"
        bg="#0a0a0b"
        border="1px solid"
        borderColor="whiteAlpha.100"
        boxShadow="0 24px 64px rgba(0,0,0,0.65)"
        position="relative"
      >
        <IconButton
          aria-label={t("common.close")}
          icon={<IoClose size={22} color="rgba(255,255,255,0.9)" />}
          position="absolute"
          right={3}
          zIndex={20}
          borderRadius="full"
          bg="whiteAlpha.200"
          minW="44px"
          w="44px"
          h="44px"
          boxShadow="0 4px 16px rgba(0,0,0,0.35)"
          _hover={{ bg: "whiteAlpha.300" }}
          _active={{ bg: "whiteAlpha.400", transform: "scale(0.96)" }}
          onClick={onClose}
          sx={{
            top: "max(10px, env(safe-area-inset-top, 0px))",
          }}
        />

        <ModalBody
          px={{ base: 4, sm: 5 }}
          pt={{ base: 6, sm: 5 }}
          pb={{ base: "max(20px, env(safe-area-inset-bottom))", sm: 5 }}
          overflowY="auto"
          position="relative"
        >
          <Flex direction="column" align="center" pt={6} pb={2} mb={3}>
            <Image
              src="/logo192.png"
              alt=""
              boxSize="64px"
              borderRadius="2xl"
              boxShadow="0 8px 24px rgba(163,230,53,0.25)"
            />
          </Flex>

          <Text
            as="h2"
            fontSize="xl"
            fontWeight="bold"
            color="white"
            textAlign="center"
            lineHeight="1.3"
            letterSpacing="-0.02em"
            mb={2}
            px={1}
          >
            {t("paywall.accessHeadline")}{" "}
            <Text as="span" color="brand.400">
              {t("paywall.accessHighlight")}
            </Text>
          </Text>

          <Text
            fontSize="sm"
            color="whiteAlpha.700"
            textAlign="center"
            lineHeight="1.55"
            mb={4}
            px={2}
          >
            {t("paywall.featuresParagraph")}
          </Text>

          <Stack spacing={2.5} mb={4} overflow="visible">
            {PLAN_ORDER.map((planKey) => (
              <PlanCard
                key={planKey}
                planKey={planKey}
                plan={PREMIUM_PLANS[planKey]}
                selected={selectedPlan === planKey}
                onSelect={setSelectedPlan}
                t={t}
              />
            ))}
          </Stack>

          <Text fontSize="sm" color="whiteAlpha.650" fontWeight="medium" textAlign="center" mb={3}>
            {billingNote}
          </Text>

          {needsBirthDate ? (
            <Box
              mb={3}
              p={3}
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.200"
              bg="whiteAlpha.50"
            >
              <Text fontSize="xs" color="whiteAlpha.800" mb={2} fontWeight="semibold">
                {t("paywall.birthDateRequired")}
              </Text>
              <Text fontSize="xs" color="whiteAlpha.600" mb={3} lineHeight="tall">
                {t("paywall.birthDateRequiredHint")}
              </Text>
              <BirthDatePicker
                value={birthDate}
                onChange={setBirthDate}
                min={minBirthDate}
                max={maxBirthDate}
                isInvalid={birthDate !== "" && !birthDateValid}
              />
              {birthDate && birthDateValid ? (
                <Text fontSize="xs" color="brand.300" mt={2}>
                  {t("getStarted.ageCalculated", { age: birthAge })}
                </Text>
              ) : null}
            </Box>
          ) : null}

          <Text fontSize="xs" color="whiteAlpha.600" textAlign="center" mb={3} lineHeight="tall">
            <Trans
              i18nKey="paywall.policyLinks"
              components={{
                terms: (
                  <Link as={RouterLink} to="/terms#terms" color="brand.300" textDecoration="underline" />
                ),
                privacy: (
                  <Link as={RouterLink} to="/terms#privacy" color="brand.300" textDecoration="underline" />
                ),
                refund: (
                  <Link as={RouterLink} to="/terms#refund" color="brand.300" textDecoration="underline" />
                ),
                aup: (
                  <Link as={RouterLink} to="/terms#aup" color="brand.300" textDecoration="underline" />
                ),
              }}
            />
          </Text>

          <Checkbox
            isChecked={purchaseConsent}
            onChange={(e) => setPurchaseConsent(e.target.checked)}
            alignItems="flex-start"
            mb={3}
            colorScheme="brand"
            size="md"
          >
            <Text fontSize="xs" color="whiteAlpha.750" lineHeight="tall" pl={1}>
              {t("paywall.purchaseConsent")}
            </Text>
          </Checkbox>

          {geoBlocked ? (
            <Text fontSize="xs" color="orange.300" textAlign="center" mb={3}>
              {t("paywall.geoBlockedBody")}
            </Text>
          ) : null}

          <Button
            w="100%"
            h="52px"
            borderRadius="2xl"
            fontSize="md"
            fontWeight="bold"
            isLoading={busy}
            loadingText={t("paywall.continueLoading")}
            isDisabled={
              !purchaseConsent ||
              geoBlocked ||
              !checkoutReady ||
              (needsBirthDate && !birthDateValid)
            }
            bgGradient="linear(to-r, brand.300, brand.500)"
            color="gray.900"
            _hover={{ bgGradient: "linear(to-r, brand.400, brand.600)" }}
            _active={{ transform: "scale(0.99)" }}
            onClick={handleContinue}
          >
            {continueLabel}
          </Button>          <Text
            fontSize="xs"
            color="whiteAlpha.500"
            textAlign="center"
            mt={4}
            mb={2}
            textTransform="uppercase"
            letterSpacing="0.08em"
            fontWeight="semibold"
          >
            {t("paywall.mobileMoneyDivider")}
          </Text>

          <Text
            fontSize="sm"
            color="#FFD56A"
            textAlign="center"
            lineHeight="1.55"
            mb={1}
            px={1}
            fontWeight="medium"
          >
            <Trans
              i18nKey="paywall.mobileMoneyHint"
              components={{
                instagram: (
                  <Text
                    as="a"
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="#FFE08A"
                    fontWeight="bold"
                    textDecoration="underline"
                    textUnderlineOffset="2px"
                    _hover={{ color: "white" }}
                  />
                ),
              }}
            />
          </Text>

          <Text fontSize="xs" color="whiteAlpha.500" textAlign="center" mt={3} lineHeight="1.5">
            {t("paywall.footerLegal")}
          </Text>

          {!checkoutReady ? (
            <Text fontSize="10px" color="orange.300" textAlign="center" mt={2}>
              {t("paywall.priceIdsMissing")}
            </Text>
          ) : null}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
